import type { getPOI$result } from '$houdini';
import type {
	AccommodationMichelinPoiData,
	AccommodationPoiData,
	PoiCategory,
} from '$lib/components/pois/types';
import type { FAQData, SEOData } from '$lib/components/seo/types';
import type { AvailableLanguageTag } from '$paraglide/runtime';
import type { PageLoadEvent } from './$types';
import { isAccommodationMichelinPoi, isAccommodationPoi } from '$lib/components/pois/utils';
import {
	getLinkSegmentsFromHierarchy,
	getLinkSegmentsFromNearbyCities,
} from '$lib/components/seo/linkSegments';
import { i18nPath, segments2routeSegments } from '$lib/i18n/routing';
import { fillHotelData } from '$lib/seo/faq/maps';
import { getRankedCitiesLinks } from '$lib/seo/footerRankedCities';
import { buildDatasheetSchema } from '$lib/seo/structuredDatas';
import { isVanitySearchParams } from '$lib/services/urls/vanity';
import { filterUndefined } from '$lib/utils/predicates';
import { isDatasheetSEOPoiCategory } from './category';

interface FormatPageMetaArgs {
	label: string;
	poiName: string;
	city?: string;
	postalCode?: string;
}

function formatPageMeta({ label, poiName, city, postalCode }: FormatPageMetaArgs): string {
	return label
		.replaceAll('{poiName}', poiName)
		.replaceAll('{city}', city ?? '')
		.replaceAll('{postalCode}', postalCode ?? '');
}

interface GetFaqEntriesArgs {
	faqData: FAQData[];
	poi: AccommodationMichelinPoiData | AccommodationPoiData;
}

function getFaqEntries({ faqData, poi }: GetFaqEntriesArgs) {
	return faqData
		.map((faqEntry) => {
			if (faqEntry.id === 'rating' && !poi.review?.score) {
				return undefined;
			}

			if (faqEntry.id === 'cost' && !poi.startingPrice) {
				return undefined;
			}

			if (faqEntry.id === 'equipments' && !poi.facilities?.length) {
				return undefined;
			}

			return fillHotelData({
				answer: faqEntry.response,
				question: faqEntry.question,
				hotelName: poi.name,
				rating: poi.review?.score ?? 0,
				city: poi.address.city ?? '',
				priceMin: poi?.startingPrice?.amount ?? 0,
				currency: poi?.startingPrice?.currency ?? '',
				equipments: poi.facilities ?? [],
			});
		})
		.filter(filterUndefined);
}

interface GetPageSeoDataArgs {
	category: PoiCategory;
	poiFromId?: getPOI$result['getPOIFromId'];
	lang: AvailableLanguageTag;
}

export function getPageSeoData(
	event: PageLoadEvent,
	{ category, poiFromId, lang }: GetPageSeoDataArgs,
) {
	if (!poiFromId || !isDatasheetSEOPoiCategory(category)) {
		return undefined;
	}

	const { description: rawDescription, title: rawTitle, faqPageData, rankedCitiesData } = event.data;

	const { cityHierarchy, nearbyCitiesHierarchies, poi } = poiFromId;

	const addressLinks = nearbyCitiesHierarchies
		? getLinkSegmentsFromNearbyCities(nearbyCitiesHierarchies)
		: undefined;

	const currentAddress = cityHierarchy ? getLinkSegmentsFromHierarchy(cityHierarchy) : undefined;

	const showAlternates = isVanitySearchParams(event.url);

	const title = formatPageMeta({
		label: rawTitle,
		poiName: poi.name,
		city: poi.address.city ?? undefined,
		postalCode: poi.address.zipCode ?? undefined,
	});

	const description = formatPageMeta({
		label: rawDescription,
		poiName: poi.name,
		city: poi.address.city ?? undefined,
		postalCode: poi.address.zipCode ?? undefined,
	});

	const vanityPath = `/map/${category}/poi/${poi.urlIdentifier}`;

	// Always show a canonical link tag if the current page is not the canonical page
	const canonicalLink = isVanitySearchParams(event.url)
		? event.url.pathname !== i18nPath(vanityPath, lang)
			? i18nPath(vanityPath, lang)
			: undefined
		: i18nPath(vanityPath, lang);

	return {
		header: {
			// Always allow indexing of SEO datasheet pages
			allowIndex: true,
			pathWithoutAddress: vanityPath,
			canonicalLink,
			showAlternates,
			og: {
				title,
				description,
			},
			structuredDatas: buildDatasheetSchema(poiFromId.poi),
		},
		footer: {
			title,
			description,
			crumbs: [
				...segments2routeSegments(event.url.pathname.split('/'), lang),
				...(currentAddress ?? []),
				{ label: poi.name, value: poi.urlIdentifier },
			],
			data: {
				type: 'structured',
				addressLinks,
				currentAddress,
				rankedCitiesData: getRankedCitiesLinks(lang, currentAddress, rankedCitiesData),
			},
			faq:
				faqPageData && (isAccommodationPoi(poi) || isAccommodationMichelinPoi(poi))
					? getFaqEntries({
							poi,
							faqData: faqPageData,
						})
					: undefined,
		},
		arrivalHierarchyPath: poiFromId.cityHierarchy ? poiFromId.cityHierarchy.map(({ value }) => value).join('/') : undefined,
	} satisfies SEOData;
}
